<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import DatePicker from '~/components/form-elements/DatePicker.vue'
import YesNoSwitch from '~/components/form-elements/YesNoSwitch.vue'
import SupplierSelect from '~/components/form-elements/select/SupplierSelect.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import { useExporting } from '~/composables/exporting'
import AuthScope from '~/enums/auth-scope'
import Export from '~/enums/export'
import type { DateTimeRange } from '~/types/date-time'
import type { SelectedItem } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  authScope: AuthScope
}>()

type Data = {
  isExtended: string
  orderCreatedAfter: string
  orderCreatedBefore: string
  supplierId?: string
  timeZone?: string
}

type Form = {
  dateTimeRange: DateTimeRange | null
  isExtended: boolean
  supplier: SelectedItem | null
}

const messages = {
  dateOrDateRangeIsNotSelected: 'Не выбрана дата или диапазон дат.'
}

const { $toast } = useNuxtApp()
const { exportData } = useExporting()

const form = ref<Form>({ dateTimeRange: null, isExtended: false, supplier: null })
const isExporting = ref(false)

function close (): void {
  model.value = false
}

async function download (): Promise<void> {
  const { dateTimeRange, isExtended, supplier } = form.value

  if (!dateTimeRange) {
    $toast.error(messages.dateOrDateRangeIsNotSelected)

    return
  }

  const { formattedDateTime1, formattedDateTime2, timeZone } = dateTimeRange

  const data: Data = {
    isExtended: isExtended ? 'yes' : 'no',
    orderCreatedAfter: formattedDateTime1,
    orderCreatedBefore: formattedDateTime2,
    timeZone
  }

  if (props.authScope === AuthScope.Admin && supplier) {
    data.supplierId = supplier.id
  }

  isExporting.value = true

  await exportData(props.authScope, Export.IncreasedRatesStatistics, 'Статистика по повышенным процентам.xlsx', data)

  isExporting.value = false
}

function setDateTimeRange (x: DateTimeRange | null): void {
  form.value.dateTimeRange = x
}
</script>

<template>
  <CustomModalWindow v-model="model">
    <template #title>
      Экспорт статистики по&nbsp;повышенным процентам
    </template>

    <DatePicker class="mb-6" is-range-mode @set-date-time-range="setDateTimeRange" />

    <SupplierSelect v-if="authScope === AuthScope.Admin" v-model="form.supplier" class="mb-6" />

    <YesNoSwitch v-model="form.isExtended" class="mb-6" label-text="Расширенная статистика" />

    <BasicButton color="green" :is-loading="isExporting" title="Скачать таблицу (XLSX)" @click="download" />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
